import { useGetMillwrightEvaluationsQuery } from '@/apis/evaluations/evaluationsApi';
import {
  LoadingSpinner,
  Stack,
  Typography,
  useWindowSize,
} from '@data-products-and-ai/react-components';
import { FC } from 'react';
import {
  INITIAL_PAGE,
  INITIAL_PAGE_SIZE,
  SPINNER_SIZE,
  TABLET_WIDTH,
} from '@/constants';
import {
  MillwrightEvaluation,
  MillwrightEvaluationFilters,
} from '@/pages/Millwrights/[ID]/components';
import {
  EvaluationFilterType,
  useEvaluationFilters,
} from '@/pages/Millwrights/[ID]/hooks';
import { usePagination } from '@/hooks';
import { Pagination } from '@/components';
import { Evaluation } from '@/apis/evaluations/types/evaluationsApi';

import '@/pages/Millwrights/[ID]/components/MillwrightEvaluations/MillwrightEvaluations.scss';

interface MillwrightEvaluationsProps {
  millwrightId: number;
  outageId?: number;
}

export const MillwrightEvaluations: FC<MillwrightEvaluationsProps> = ({
  millwrightId,
  outageId,
}) => {
  const { width } = useWindowSize();
  const isSmallerScreen = width <= TABLET_WIDTH;

  const { currentPage, pageSize, setPage } = usePagination({
    initialPage: INITIAL_PAGE,
    initialPageSize: INITIAL_PAGE_SIZE,
  });

  const { evaluationFilter, updateEvaluationFilter } = useEvaluationFilters();

  const {
    data: evaluationsData,
    isLoading,
    isFetching,
  } = useGetMillwrightEvaluationsQuery(
    {
      empId: millwrightId,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
      ...(evaluationFilter === 'Current' && { outageId }),
    },
    {
      skip: !millwrightId,
    },
  );
  const evaluations = evaluationsData?.results || [];
  const totalItems = evaluationsData?.total ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const loadingState = isLoading || isFetching;

  const handlePageChange = (page: number) => {
    setPage(page);

    if (isSmallerScreen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleEvaluationFilterChange = (filterType: EvaluationFilterType) => {
    updateEvaluationFilter(filterType);
  };

  const mainContent =
    (evaluations?.length ?? 0) > 0 ? (
      <>
        <div className="millwright-evaluations__items">
          {evaluations?.filter(Boolean).map((evaluation, index) => (
            <MillwrightEvaluation
              key={`evaluation-${index}-${evaluation?.evalDate}`}
              evaluation={evaluation as Evaluation}
            />
          ))}
        </div>
        <Stack>
          <Pagination
            disabled={isFetching}
            currentPage={currentPage}
            totalPages={totalPages}
            maxVisiblePages={isSmallerScreen ? 3 : 5}
            onPageChange={handlePageChange}
          />
        </Stack>
      </>
    ) : (
      <Stack padding={32}>
        <Typography color="var(--color-black)" tag="textdefault_strong">
          No evaluations available
        </Typography>
      </Stack>
    );

  return (
    <div className="millwright-evaluations">
      <Stack gap={16} direction="row" horizontalAlign="center">
        <Typography color="var(--color-black)" tag="h3">
          Evaluations
        </Typography>
        <Typography color="var(--color-default)" tag="textdefault">
          {totalItems}
        </Typography>
      </Stack>
      <MillwrightEvaluationFilters
        filterType={evaluationFilter}
        onFilterChange={handleEvaluationFilterChange}
      />
      {loadingState ? (
        <LoadingSpinner
          size={SPINNER_SIZE}
          text="Loading evaluations..."
          color="var(--color-primary)"
          align="center"
          tag="textdefault"
          inline
        />
      ) : (
        mainContent
      )}
    </div>
  );
};
